import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { CallMediaPermissionValue } from '@gem/call/CallStore/Call.types';
import { MeStore } from '@gem/call/CallStore/Me/Me.store';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import {
  ICallPersonalizedPermissions,
  PermissionsPersonalizedValue,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { ElectronApi } from '@gem/call/Removable/packages/electron/ElectronApi';
import { PERMISSIONS_REQUEST_TYPES } from '@gem/call/constants';
import { getDesktopScreenForSharing } from '@gem/call/tasks/getDesktopScreenForSharing';
import { useTranslation } from '@gem/modules/i18n';
import { popupsTasks } from '@gem/popups';
import { platformInfo } from '@gem/utils';

interface ICallActionsPanelPresenter {
  ongoingCall: Call;
  permissions: ICallPersonalizedPermissions | null;
  screenPermissions: PermissionsPersonalizedValue | null;
  meStore: MeStore;
  isShowRiseHand: boolean;
  micControlEnabled: boolean;
  isShowControlCamera: boolean;
  isShowControlScreenSharing: boolean;
  isSharedScreen: boolean;
  isCallStarted: boolean;
  isUnauthorizedCall: boolean;
  channelId: string | null;
  isMeInitiatorOwnerOrAdmin: boolean;
  isHandRaised: boolean;
  audioPermission: CallMediaPermissionValue;
  videoPermission: CallMediaPermissionValue;
  showAudioPermissionWarning(): void;
  showVideoPermissionWarning(): void;
  checkAndSendPermissionRequest(permissionType: PermissionsTypes): Promise<boolean>;
  shareScreen(track?: MediaStreamTrack): Promise<void>;
  stopSharingScreen(): Promise<void>;
  toggleHand(): void;
}

export const useCallActionsPanelPresenter = (): ICallActionsPanelPresenter => {
  const { t } = useTranslation();
  const context = useCallContext();

  const presenter = useLocalObservable<ICallActionsPanelPresenter>(() => ({
    get ongoingCall(): Call {
      return context.call;
    },

    get audioPermission(): CallMediaPermissionValue {
      return presenter.ongoingCall.audioPermission;
    },

    get videoPermission(): CallMediaPermissionValue {
      return presenter.ongoingCall.videoPermission;
    },

    get permissions(): ICallPersonalizedPermissions | null {
      const permissions = presenter.ongoingCall.permissions;

      return (
        permissions?.getMemberPersonalizedPermissions(permissions.personalizedPermissions) || null
      );
    },

    get screenPermissions(): PermissionsPersonalizedValue | null {
      return presenter.permissions && presenter.permissions[PermissionsTypes.ShareScreen]
        ? presenter.permissions[PermissionsTypes.ShareScreen]
        : null;
    },

    get meStore(): MeStore {
      return presenter.ongoingCall.meStore;
    },

    get isShowRiseHand(): boolean {
      return presenter.ongoingCall.uiFeatures.raiseHandControlEnabled;
    },

    get micControlEnabled(): boolean {
      return !presenter.isCallStarted || presenter.ongoingCall.uiFeatures.showControlMic;
    },

    get isShowControlCamera(): boolean {
      return !presenter.isCallStarted || presenter.ongoingCall.uiFeatures.showControlCamera;
    },

    get isShowControlScreenSharing(): boolean {
      return (
        !platformInfo.isMobile &&
        !platformInfo.isTablet &&
        presenter.ongoingCall.uiFeatures.showControlScreenSharing
      );
    },

    get isSharedScreen(): boolean {
      return presenter.meStore.isSharedScreen;
    },

    get isCallStarted(): boolean {
      return presenter.ongoingCall.isStarted;
    },

    get isUnauthorizedCall(): boolean {
      return presenter.ongoingCall.isUnauthorizedCall;
    },

    get channelId(): string | null {
      return presenter.ongoingCall.channelId || null;
    },

    get isMeInitiatorOwnerOrAdmin(): boolean {
      return presenter.ongoingCall.isMeInitiatorOwnerOrAdmin;
    },

    get isHandRaised(): boolean {
      return presenter.meStore.isHandRaised;
    },

    showVideoPermissionWarning(): void {
      popupsTasks.showAlert(t('calls_no_camera_permissions_web'));
    },

    showAudioPermissionWarning(): void {
      popupsTasks.showAlert(t('calls_no_microphone_permissions_web'));
    },

    async checkAndSendPermissionRequest(permissionType: PermissionsTypes): Promise<boolean> {
      const permissionValue = presenter.permissions
        ? presenter.permissions[permissionType]
        : undefined;

      if (!permissionValue) {
        return false;
      }

      if (
        permissionValue === PermissionsPersonalizedValue.Always ||
        presenter.isMeInitiatorOwnerOrAdmin
      ) {
        return true;
      }

      if (permissionValue === PermissionsPersonalizedValue.Never) {
        popupsTasks.showAlert(t('call_no_permission'));

        return false;
      }

      const res = await popupsTasks.showConfirm({
        text: t('call_want_ask_for_permission'),
        primaryText: t('common_yes'),
        secondaryText: t('common_no'),
      });

      if (!res) {
        return false;
      }

      await context.callController.createPermissionRequest({
        access: PERMISSIONS_REQUEST_TYPES[permissionType],
        channelId: presenter.channelId!,
      });

      return false;
    },

    async shareScreen(): Promise<void> {
      const canShareScreen = await presenter.checkAndSendPermissionRequest(
        PermissionsTypes.ShareScreen
      );

      if (!canShareScreen) {
        return;
      }

      let electronSourceId: string | null = null;

      if (ElectronApi.isEnabled) {
        electronSourceId = await getDesktopScreenForSharing();

        if (!electronSourceId) {
          return;
        }
      }

      await context.callController.startScreenSharing(electronSourceId || undefined);
    },

    async stopSharingScreen(): Promise<void> {
      await context.callController.stopScreenSharing();
    },

    toggleHand(): void {
      context.callController.toggleHand();
    },
  }));

  useEffect(() => {
    if (
      presenter.isSharedScreen &&
      presenter.screenPermissions !== PermissionsPersonalizedValue.Always &&
      !presenter.isMeInitiatorOwnerOrAdmin
    ) {
      presenter.stopSharingScreen();
    }
  }, [presenter.isSharedScreen, presenter.screenPermissions, presenter.isMeInitiatorOwnerOrAdmin]);

  return presenter;
};
